import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import classes from "./FormDateDurationSelector.scss";
import moment from "moment";
import { DatePicker } from "@toddle-edu/ds-web";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

class FormDateDurationSelector extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  componentDidMount = () => {
    const {
      value: { endDate, startDate },
      maxDate,
      minDate,
    } = this.props;

    const isStartDateValid = moment(startDate).isValid();
    const isEndDateValid = moment(endDate).isValid();

    // if start and end date is not valid and then set them equal to
    // start and end date of Academic Year
    if (!isStartDateValid && !isEndDateValid) {
      this.changeDate({ startDate: minDate, endDate: maxDate });
      return;
    }

    if (moment(endDate).isAfter(moment(maxDate)) || !isEndDateValid) {
      this.changeDate({ endDate: maxDate });
    }

    if (moment(startDate).isBefore(moment(minDate)) || !isStartDateValid) {
      this.changeDate({ startDate: minDate });
    }
  };

  changeDate = params => {
    const {
      value,
      maxDate,
      minNumOfDays,
      shouldChangeEndDateWithStartDate,
      minDate,
      disabled,
    } = this.props;

    if (disabled) return;

    const { endDate } = value;

    if (
      _.has(params, "startDate") &&
      moment(params.startDate).isBefore(moment(minDate))
    ) {
      params["startDate"] = minDate;
    }

    if (
      _.has(params, "startDate") &&
      moment(params.startDate).isAfter(moment(endDate))
    ) {
      params["endDate"] = moment(params.startDate).add(minNumOfDays, "days");
    }

    // When we need the end Date to be made start Date + minNumOfDays, automatically when startDate is changed
    //        shouldChangeEndDateWithStartDate = true
    //        the number of days difference between dates = minNumOfDays
    //
    if (_.has(params, "startDate") && shouldChangeEndDateWithStartDate) {
      params["endDate"] = moment(params.startDate).add(minNumOfDays, "days");
    }

    if (
      _.has(params, "endDate") &&
      moment(params.endDate).isAfter(moment(maxDate))
    ) {
      params["endDate"] = maxDate;
    }

    this.updateValue({ ...value, ...params });
  };

  isValid = () => {
    const { value, customValidation } = this.props;
    const { startDate, endDate } = value;

    if (customValidation) {
      const customError = customValidation(value);
      if (customError != "") {
        this.setState({ error: customError });
        return 1;
      } else {
        return 0;
      }
    } else {
      const isStartDateValid = moment(startDate).isValid();
      const isEndDateValid = moment(endDate).isValid();

      return !isStartDateValid + !isEndDateValid;
    }
  };

  renderEdit = () => {
    const {
      t,
      minDate,
      maxDate,
      startDateLabel,
      endDateLabel,
      startDateName,
      endDateName,
      startDatePlaceholder,
      endDatePlaceholder,
      value: { startDate, endDate },
      disabled,
      dateFormat,
      size,
      variant,
      displayDisabledAsActive,
      testId,
      dsVersion,
    } = this.props;
    const isDSV2 = dsVersion === "2.0";

    /*******************************************************************************************************
     * displayDisabledAsActive flag is used to control the visual state of this component,
     * where if it is true, we will show the element in visually active form but
     * functionality wise it will behave like disabled.
     ********************************************************************************************************/

    const containerClasses = classNames(classes.container, {
      [classes.containerV2]: isDSV2,
    });

    const dateContainerClasses = classNames({
      [classes.dateContainer]: true,
      [classes.displayDisabledAsActive]: disabled && displayDisabledAsActive,
    });

    return (
      <div className={containerClasses}>
        <div className={dateContainerClasses}>
          {/*******************************************************************************************************
           * We have implemented dateContainerClasses to form a parent child relationship between our DS component
           * and this component, to include styling for displayDisabledAsActive state, so please check the css before
           * adding any wrapper element to the component and check that existing behaviour remains the same
           ********************************************************************************************************/}
          <DatePicker
            label={
              _.isEqual(startDateLabel, "common:start_date")
                ? t(startDateLabel)
                : startDateLabel
            }
            placeholder={
              _.isEqual(startDatePlaceholder, "common:start_date")
                ? t(startDatePlaceholder)
                : startDatePlaceholder
            }
            onChange={params => this.changeDate({ [startDateName]: params })}
            picker="date"
            format={dateFormat}
            value={moment(startDate)}
            size={size}
            variant={variant}
            name={startDateName}
            minDate={moment(minDate)}
            maxDate={moment(maxDate)}
            disabled={disabled && !displayDisabledAsActive}
            dsVersion={dsVersion}
            testId={`${testId}-startDate`}
          />
        </div>
        <div className={dateContainerClasses}>
          <DatePicker
            label={
              _.isEqual(endDateLabel, "common:end_date")
                ? t(endDateLabel)
                : endDateLabel
            }
            placeholder={
              _.isEqual(endDatePlaceholder, "common:end_date")
                ? t(endDatePlaceholder)
                : endDatePlaceholder
            }
            onChange={params => this.changeDate({ [endDateName]: params })}
            picker="date"
            format={dateFormat}
            value={moment(endDate)}
            size={size}
            variant={variant}
            name={endDateName}
            minDate={moment(startDate)}
            maxDate={moment(maxDate)}
            disabled={disabled && !displayDisabledAsActive}
            dsVersion={dsVersion}
            testId={`${testId}-endDate`}
          />
        </div>
      </div>
    );
  };
}

FormDateDurationSelector.defaultProps = {
  ...UIBaseComponent.defaultProps,
  minDate: null,
  maxDate: null,
  disabled: false,
  value: { startDate: null, endDate: null },
  startDateLabel: "common:start_date",
  endDateLabel: "common:end_date",
  startDateName: "startDate",
  endDateName: "endDate",
  startDatePlaceholder: "common:start_date",
  endDatePlaceholder: "common:end_date",
  minNumOfDays: 40,
  shouldChangeEndDateWithStartDate: false,
  dateFormat: DATE_TIME_CONSTANTS.D_MMM_YYYY,
  size: "large",
  variant: "outlined",
  displayDisabledAsActive: false,
  testId: "UIComponents-formDateDurationSelector",
  dsVersion: "1.0",
};

FormDateDurationSelector.propTypes = {
  ...UIBaseComponent.propTypes,
  displayDisabledAsActive: PropTypes.bool,
  testId: PropTypes.string,
  dsVersion: PropTypes.string,
};

FormDateDurationSelector.displayName = "FormDateDurationSelector";

export default I18nHOC({ resource: ["common"] })(FormDateDurationSelector);
